import { memo, useEffect, useRef } from "react";
import { selectIceberg, selectOrderType, selectStrategyBuilderError, selectTradeOrders } from "../../../selectors/strategyBuilder";
import { useSelector } from "react-redux";
import TradeOrderRow from "./tradeOrderRow/tradeOrderRow";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useStyles } from "./styles";
import { TradingAccount } from "../../../proto/generated/trading-bridge";
import { getOrderBook, getSecuritySnapshot } from "../../../selectors/security";
import { Broker_OrderType } from "../../../proto/generated/order";
import { useDispatch } from "react-redux";
import { updateTradeOrders } from "../../../reducer/strategyBuilder.reducer";
import { getSecurityKey } from "../../../util/security";
import { limitPriceFromOrderBookOrLtp } from "../../../util/orderBookPrice";

export type TradeOrdersContainerProps = {
    showCheckbox?: boolean,
    duplicateOrders?: boolean,
    tradingAccount?: TradingAccount
}

const TradeOrdersContainer: React.FC<TradeOrdersContainerProps> = ({tradingAccount}) => {
    const dispatch = useDispatch();
    const {classes} = useStyles()
    const tradeOrders = useSelector(selectTradeOrders);
    const orderType = useSelector(selectOrderType);
    const iceberg = useSelector(selectIceberg);
    const error = useSelector(selectStrategyBuilderError);

    const previousOrderType = useRef(orderType);
    useEffect(() => {
        if (previousOrderType.current !== orderType) {
            previousOrderType.current = orderType;

            if (!tradeOrders?.length) return;
            if (orderType === Broker_OrderType.MARKET_ORDER) {
                dispatch(updateTradeOrders(tradeOrders.map(t => ({...t, price: 0}))))
            } else {
                // When switching back to LIMIT, reset prices to current market prices
                dispatch(updateTradeOrders(tradeOrders.map(t => {
                    const securityKey = getSecurityKey(t.security.exchange, t.security.code);
                    const orderBook = getOrderBook(securityKey)
                    const snapshot = getSecuritySnapshot(securityKey)
                    const newPrice = limitPriceFromOrderBookOrLtp(orderBook, t.transactionType, snapshot?.lastTradedPrice)
                    return {
                        ...t,
                        price: newPrice || t.price || 0
                    }
                })))
            }
        }
    }, [orderType, tradeOrders])

    return  <>
                <Table
                    sx={
                        iceberg.enabled
                            ? { borderCollapse: "separate", borderSpacing: "0 1px", width: "100%" }
                            : undefined
                    }
                >
                    <TableHead>
                        <TableRow className={classes.headerRow}>
                            <TableCell className={classes.headerCell}></TableCell>
                            <TableCell className={classes.headerCell}>
                                <Typography color={'text.secondary'} variant={'caption'}>B/S</Typography>
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                <Typography color={'text.secondary'} variant={'caption'}>EXPIRY</Typography>
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                <Typography color={'text.secondary'} variant={'caption'}>STRIKE</Typography>
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                <Typography color={'text.secondary'} variant={'caption'}>TYPE</Typography>
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                <Typography color={'text.secondary'} variant={'caption'}>QTY</Typography>
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                <Typography color={'text.secondary'} variant={'caption'}>PRICE</Typography>
                            </TableCell>
                            <TableCell className={classes.headerCell}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tradeOrders.map(t => <TradeOrderRow order={t} selectable key={t.clientOrderId}  tradingAccount={tradingAccount} />)}
                        {error && (
                            <TableRow>
                                <TableCell colSpan={8} className={classes.tableCell}>
                                    <Typography fontSize={'14px'} textAlign="left" color={'red'} pl={'6px'}>{error}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                
            </>
};

export default memo(TradeOrdersContainer);